import { TextConstants } from "@shared/constants";

import { CommonUtils } from "./common.utils";
import { RulesetResourceTypeEnum } from "../enums";
import {
  IConditionAny,
  IConditionEq,
  IDocumentType,
  IExpectationGroup,
  IMissingDocumentType,
  IRuleset,
} from "../interfaces";

const getExpectationsGroups = (rulesets: IRuleset[]): IExpectationGroup[] => {
  const expectationGroupsMap: { [key: string]: IExpectationGroup } = {};

  rulesets.forEach((ruleset) => {
    ruleset.expectations?.forEach((expectation) => {
      const { path, condition, conditionsMet } = expectation;
      const key = `${path}_${conditionsMet}`;

      if (!expectationGroupsMap[key]) {
        expectationGroupsMap[key] = {
          path,
          conditions: [],
          conditionsMet,
        };
      }

      expectationGroupsMap[key].conditions.push(condition);
    });
  });

  return Object.values(expectationGroupsMap) as IExpectationGroup[];
};

const getDocumentTypesWithRulesets = (
  rulesets: IRuleset[],
  conditionsMet: boolean,
  allDocumentTypes: IDocumentType[],
): IMissingDocumentType[] => {
  const result: IMissingDocumentType[] = [];

  filterRulesetsByConditionsMet(rulesets, conditionsMet).forEach((ruleset) => {
    const unmetExpectations = ruleset.expectations.filter((exp) => !exp.conditionsMet);

    unmetExpectations.forEach((expectation) => {
      (expectation.condition?.and || []).forEach((andCondition) => {
        const typeValues =
          (andCondition as IConditionAny).any?.value ||
          (andCondition as IConditionEq).eq?.value ||
          [];

        let typesString: string;

        if (Array.isArray(typeValues)) {
          const types = typeValues.map((type) =>
            allDocumentTypes.find((t) => t.id === CommonUtils.getUriId(type)),
          );

          typesString = types.map((t) => t.name).join(" / ");
        } else {
          const type = allDocumentTypes.find((t) => t.id === CommonUtils.getUriId(typeValues));

          typesString = type.name;
        }

        if (typesString) {
          result.push({
            ruleset: {
              id: ruleset.id,
              name: ruleset.name,
            },
            name: typesString,
            types: typesString,
          });
        }
      });
    });
  });

  return result;
};

const filterRulesetsByConditionsMet = (
  rulesets: IRuleset[],
  conditionsMet: boolean,
): IRuleset[] => {
  return rulesets
    .filter((ruleset) =>
      ruleset.expectations.some((expectation) => expectation.conditionsMet === conditionsMet),
    )
    .map((ruleset) => {
      const filteredExpectations = ruleset.expectations.filter(
        (expectation) => expectation.conditionsMet === conditionsMet,
      );

      return {
        ...ruleset,
        expectations: filteredExpectations,
      };
    });
};

const getResourceRoute = (resourceType: RulesetResourceTypeEnum): string => {
  switch (resourceType) {
    case RulesetResourceTypeEnum.CONNECTION:
      return "connections";
    case RulesetResourceTypeEnum.LOCATION:
      return "locations";
    case RulesetResourceTypeEnum.DELIVERY:
      return "deliveries";
    default:
      throw new Error(TextConstants.UNSUPPORTED_RESOURCE_TYPE);
  }
};

export const RulesetUtils = {
  getExpectationsGroups,
  filterRulesetsByConditionsMet,
  getDocumentTypesWithRulesets,
  getResourceRoute,
};
